import React from 'react';
import { Box, Grid, Typography } from '@mui/material';

type ResourceVariant =
    | 'home'
    | 'infographics'
    | 'illustrations'
    | 'publications'
    | 'videos'
    | 'articles'
    | 'blogs';

const RESOURCE_CONTENT: Record<ResourceVariant, {
    eyebrow: string;
    heading: string;
    body: string;
    points: string[];
}> = {
    home: {
        eyebrow: 'Educational purpose',
        heading: 'Original visual learning for radiology and anatomy',
        body: 'Radiology Illustration is built around original diagrams, concise explanations, and structured teaching resources. The goal is to turn dense radiology topics into visual, memorable learning material that can be reviewed by students, residents, educators, and clinicians.',
        points: [
            'Illustrations highlight anatomy, pathology, and imaging relationships in a way that is easier to recall than text alone.',
            'Infographics summarize classifications, reporting clues, staging systems, and practical diagnostic frameworks.',
            'Articles and videos add context, caveats, and teaching notes so the images are educational rather than decorative.',
        ],
    },
    infographics: {
        eyebrow: 'How to use this section',
        heading: 'Infographics for fast, structured revision',
        body: 'The infographics section focuses on high-yield summaries that are useful during exam preparation, lecture revision, and quick clinical review. Each visual is designed to condense a specific concept without losing the radiology context that makes it useful.',
        points: [
            'Use the search field to find disease entities, named signs, staging systems, and anatomy topics.',
            'Open each collection for related figures and supporting captions that explain the visual checklist.',
            'Treat the graphics as educational summaries and pair them with standard textbooks, guidelines, and supervised clinical learning.',
        ],
    },
    illustrations: {
        eyebrow: 'How to use this section',
        heading: 'Illustrations that connect anatomy with imaging',
        body: 'The radiology illustration library emphasizes anatomy, pathology, and imaging patterns that are easier to understand visually. These resources are created for teaching, self-study, and concept reinforcement.',
        points: [
            'Browse collections by topic to see how structures, lesions, and imaging signs relate spatially.',
            'Use captions and descriptions to understand what the illustration is emphasizing.',
            'Return to the illustrations during case review when a visual mental model can clarify difficult anatomy.',
        ],
    },
    publications: {
        eyebrow: 'Academic context',
        heading: 'Published work and research references',
        body: 'The publications section collects academic work, books, and research-linked resources associated with Dr. Shubham Arora. It helps readers connect visual teaching material with formal scholarly output.',
        points: [
            'Publication entries link outward only when a source or book page is available.',
            'Use this section to review author background, areas of academic interest, and related radiology topics.',
            'External publication pages remain governed by their own publishers, platforms, and access policies.',
        ],
    },
    videos: {
        eyebrow: 'Learning format',
        heading: 'Short videos for focused radiology review',
        body: 'Short videos provide concise explanations for radiology signs, image interpretation patterns, and practical learning points. They are meant for quick review rather than replacing full lectures or supervised training.',
        points: [
            'Open video entries for rapid topic refreshers and sign-based learning.',
            'Use the videos alongside diagrams and articles for a stronger visual memory of each concept.',
            'Content is educational and should not be used as individual medical advice.',
        ],
    },
    articles: {
        eyebrow: 'Reading guide',
        heading: 'Articles with clinical teaching context',
        body: 'The article library adds written context around radiology concepts, cases, and image interpretation. Articles are intended to explain why a sign matters, where a visual pattern fits, and how a learner can approach the topic systematically.',
        points: [
            'Use tabs to browse articles, blogs, or the combined archive.',
            'Open individual articles for sections, figures, captions, and references when available.',
            'The writing is educational and should be interpreted within formal medical training and local clinical standards.',
        ],
    },
    blogs: {
        eyebrow: 'Perspective',
        heading: 'Blog notes on radiology learning and teaching',
        body: 'Blog posts contain educational reflections, teaching experiences, and thoughts on visual communication in radiology. They support the main illustration and article library by adding author perspective.',
        points: [
            'Browse posts for practical study ideas, academic reflections, and visual teaching notes.',
            'Use blog entries as supporting material rather than formal clinical guidance.',
            'When a post references medical concepts, apply the same educational-only disclaimer used across the site.',
        ],
    },
};

type ResourceLearningSummaryProps = {
    variant: ResourceVariant;
};

const ResourceLearningSummary: React.FC<ResourceLearningSummaryProps> = ({ variant }) => {
    const content = RESOURCE_CONTENT[variant];

    return (
        <Box
            component="section"
            sx={{
                maxWidth: 1100,
                mx: 'auto',
                my: { xs: 3, md: 5 },
                px: { xs: 1, sm: 2 },
            }}
        >
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                {content.eyebrow}
            </Typography>
            <Typography
                variant="h2"
                sx={{
                    fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2rem' },
                    fontWeight: 700,
                    mb: 1,
                    lineHeight: 1.25,
                }}
            >
                {content.heading}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                {content.body}
            </Typography>
            <Grid container spacing={2}>
                {content.points.map((point) => (
                    <Grid key={point} size={{ xs: 12, md: 4 }}>
                        <Box
                            sx={{
                                height: '100%',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 2,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                {point}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ResourceLearningSummary;
